// Catalog.ID Client — Account API
// ============================================================================

import { api, ApiError } from './client';
import type {
    BeginRegisterRequest, BeginRegisterResponse,
    RegisterRequest, RegisterResponse,
    LoginRequest, LoginResponse,
    KeyBagResponse, ReplaceKeyBagRequest, KeyBagEntryInput,
    PublicKeyInfo,
    AccountInfo,
    TerminateRequest, TerminateResponse,
    StepUpChallengeResponse,
    RequestUnlockRequest, ApproveUnlockRequest,
    PendingUnlockAccount,
    RotateKeysRequest, RotateKeysResponse, RotateKeysChallengeResponse,
    TransferProfileRequest,
    TotpRevealResponse, LoginOrTotpResponse,
} from './types';

/** Step 1: reserve a CID for this payment proof so we can compute Argon2id(password, SHA-256(cid)). */
/** A personal registration runs at the ENTRY node — the node this client
 *  started from — whoever else may be logged in on this device: the new
 *  account is originated there (docs/cid-governance.md M7a), and vouchers and
 *  invitations are redeemed at the node they were made at. */
export async function beginRegister(req: BeginRegisterRequest): Promise<BeginRegisterResponse> {
    return api.post('/api/account/register/begin', req, { anonymous: true });
}

export async function register(req: RegisterRequest): Promise<RegisterResponse> {
    return api.post('/api/account/register', req, { anonymous: true });
}

/**
 * Password login. Answers EITHER the ordinary session response OR, when the
 * account's Authenticator Key login gate is on (registration-restructure
 * §15.5), `{ status: 'totp_required', preAuthToken, ttlSeconds }` — no session.
 * Discriminate with {@link isTotpRequired} and finish through {@link loginTotp}.
 *
 * Sent to the account's origin (docs/cid-governance.md D18, M7a): every session
 * is created there. The caller resolves the origin first (`resolveOrigin`);
 * should a node still answer `origin_only`, the client moves the identity to
 * the origin it names and asks there, once.
 */
export async function login(req: LoginRequest): Promise<LoginOrTotpResponse> {
    return api.post('/api/account/login', req, { cid: req.cid, anonymous: true });
}

export async function logout(): Promise<void> {
    return api.post('/api/account/logout');
}

/**
 * End ONE identity's session using its own stored token rather than the active
 * one. "Log Out" ends every identity's session on this device, and each has its
 * own token; the active token cannot speak for the others.
 */
export async function logoutWithToken(sessionToken: string, cid: string): Promise<void> {
    return api.post('/api/account/logout', undefined, { token: sessionToken, cid });
}

export async function lockdown(): Promise<void> {
    return api.post('/api/account/lockdown');
}

/**
 * Mark THIS session's account as having finished onboarding (registration-
 * restructure §8.5). Session-only and idempotent; returns an empty 200.
 *
 * Called by the wizards' type-back print gate, and only by it: an account that
 * never receives this call is purged by the server after 7 days (status
 * 'terminated', reason 'onboarding_incomplete'), which is what makes an
 * abandoned stash and a squatted half-identity worthless. A delegate founding
 * an organization calls this for itself in addition to
 * {@link import('./organization').completeOrgOnboarding} for the org.
 */
export async function completeOnboarding(): Promise<void> {
    return api.post('/api/account/onboarding/complete');
}

export async function getKeyBag(): Promise<KeyBagResponse> {
    return api.get('/api/account/keybag');
}

export async function replaceKeyBag(req: ReplaceKeyBagRequest): Promise<void> {
    return api.put('/api/account/keybag', req);
}

/** Replace the account password's auth hash. Step-up-gated ("set-authhash"
 *  purpose). Revokes as a lockdown does (docs/cid-governance.md M15a): every other
 *  session, every OIDC refresh token, and every device but `deviceToken`'s — the
 *  one the change is made from; the calling session is kept. Used by the
 *  registration finalize-at-print step and Settings password change. */
export async function setAuthHash(req: { newAuthHash: string; challengeSignature: string; deviceToken?: string }): Promise<void> {
    return api.post('/api/account/set-authhash', req);
}

// ── The Authenticator Key (registration-restructure §15.5, round 8) ─────────
// One purpose (`totp-manage`) guards every write on this surface; `enable` and
// `verify` take a live code instead, and `reveal` is the only path by which a
// stored secret ever leaves the node.

/** Store (or replace) the account's Authenticator Key. Step-up-gated
 *  (`totp-manage`). The login gate goes OFF and the replay floor is cleared, so
 *  a NEW secret has to be confirmed with a code before it gates anything.
 *
 *  TRAP (§15.8): calling this twice for one registration rotates the secret on
 *  the node and voids the sheet already printed. The finalize helper's resume
 *  rule is what prevents that — do not call it directly from a wizard. */
export async function putTotpKey(req: { secret: string; challengeSignature: string }): Promise<void> {
    return api.put('/api/account/totp/key', req);
}

/** Turn the login gate on. Session + a live code, no step-up: the code IS the
 *  proof that the member holds the phone. */
export async function enableTotp(code: string): Promise<{ enabled: boolean }> {
    return api.post('/api/account/totp/enable', { code });
}

/** Turn the login gate off — a code AND a `totp-manage` step-up, so a bare
 *  session token cannot remove the second factor. */
export async function disableTotp(
    req: { code: string; challengeSignature: string },
): Promise<{ enabled: boolean }> {
    return api.post('/api/account/totp/disable', req);
}

/** Hand the stored secret back to the account's own session under a
 *  `totp-manage` step-up: Settings shows the QR for a new phone and prints a
 *  replacement sheet from it. */
export async function revealTotp(
    req: { challengeSignature: string },
): Promise<TotpRevealResponse> {
    return api.post('/api/account/totp/reveal', req);
}

/** "Confirm with your authenticator" for any other feature. Issues nothing. */
export async function verifyTotp(code: string): Promise<{ ok: boolean }> {
    return api.post('/api/account/totp/verify', { code });
}

/** Second leg of a gated login: the pre-auth token from {@link login} plus a
 *  live code, exchanged for the ordinary login response. Anonymous. */
export async function loginTotp(
    req: { cid: string; preAuthToken: string; code: string },
): Promise<LoginResponse> {
    return api.post('/api/account/login/totp', req, { cid: req.cid, anonymous: true });
}

/**
 * Add a single entry to the current session's key bag without rewriting other entries.
 * Used by the Key Grant "save" flow when an external module (e.g. cld-xrpl) supplies a
 * freshly-generated data key it wants backed up in the bag. The caller is responsible
 * for encrypting the private-key payload under the supersecret-derived key first.
 * Backed by POST /api/account/keybag/entries.
 *
 * STEP-UP GATED (cid-encrypted-storage §10 item 7). The endpoint is an UPSERT,
 * so a bare session token was enough to overwrite an existing entry's
 * ciphertext — `main_signing_key@0` included. `challengeSignature` is a
 * `keybag-entry` challenge signed with that same key ({@link signStepUp}), and
 * it is a REQUIRED argument rather than an optional field so a caller cannot
 * forget it and discover the omission as a 400 at runtime. The challenge is
 * single-use: two writes need two signatures.
 */
export async function addKeyBagEntry(
    entry: KeyBagEntryInput, challengeSignature: string,
): Promise<void> {
    return api.post('/api/account/keybag/entries', { ...entry, challengeSignature });
}

/**
 * Change ONE key-bag entry's status without rewriting the bag. Backed by
 * `PUT /api/account/keybag/entries/{keyId}/{keyIndex}` (session-only,
 * op-logged `keybagentry update`).
 *
 * Used by a Recovery Kit reissue (registration-restructure §16.6): the new
 * generation is ADDED first, then the one it supersedes is marked `revoked`
 * here. A revoked generation is retained — anyone who can read the bag holds
 * the supersecret already — but nothing ever prints from it.
 *
 * STEP-UP GATED like the add beside it (cid-encrypted-storage §10 item 7):
 * revoking an entry is one keystroke away from stranding chain-walked data, so
 * it needs key custody, not just a session token. `challengeSignature` is a
 * SEPARATE single-use `keybag-entry` challenge — the add's cannot be reused.
 */
export async function updateKeyBagEntryStatus(
    keyId: string, keyIndex: number, status: 'active' | 'revoked',
    challengeSignature: string,
): Promise<void> {
    return api.put(
        `/api/account/keybag/entries/${encodeURIComponent(keyId)}/${keyIndex}`,
        { status, challengeSignature });
}

export async function getOwnPublicKeys(): Promise<PublicKeyInfo[]> {
    return api.get('/api/account/publickeys');
}

export async function getPublicKeys(cid: string): Promise<PublicKeyInfo[]> {
    return api.get(`/api/account/publickeys/${cid}`);
}

export async function getAccountInfo(): Promise<AccountInfo> {
    return api.get('/api/account/me');
}

export async function terminate(req: TerminateRequest): Promise<TerminateResponse> {
    return api.post('/api/account/terminate', req);
}

/** Request a single-use step-up challenge to sign before a sensitive, otherwise
 *  token-only action (purpose "terminate" or "oidc-approve"). See {@link signStepUp}. */
export async function getStepUpChallenge(purpose: string): Promise<StepUpChallengeResponse> {
    return api.post('/api/account/step-up/challenge', { purpose });
}

export async function requestUnlock(req: RequestUnlockRequest): Promise<void> {
    return api.post('/api/account/request-unlock', req, { cid: req.cid, anonymous: true });
}

export async function approveUnlock(req: ApproveUnlockRequest): Promise<void> {
    return api.post('/api/account/approve-unlock', req);
}

export async function getPendingUnlocks(): Promise<PendingUnlockAccount[]> {
    return api.get('/api/account/pending-unlocks');
}

/** Request a one-time challenge to sign before calling /rotate-keys (H-3). */
export async function getRotateKeysChallenge(): Promise<RotateKeysChallengeResponse> {
    return api.post('/api/account/rotate-keys/challenge', {});
}

export async function rotateKeys(req: RotateKeysRequest): Promise<RotateKeysResponse> {
    return api.post('/api/account/rotate-keys', req);
}

export async function transferProfile(req: TransferProfileRequest): Promise<void> {
    return api.post('/api/account/transfer-profile', req);
}

// ── Recovery Kit metadata (Recovery Kit Addendum §12/§14) ──────────────────

export interface RecoveryKitInfo {
    recoverySetId: string;
    thresholdK: number;
    totalSharesN: number;
    bundleFingerprint: string;
    createdAt: string;
    lastTestedAt: string | null;
}

/** Returns null when the account has no kit on record. */
export async function getRecoveryKit(): Promise<RecoveryKitInfo | null> {
    try {
        return await api.get('/api/account/recoverykit');
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
    }
}

export interface PutRecoveryKitRequest {
    recoverySetId: string;
    thresholdK: number;
    totalSharesN: number;
    bundleFingerprint: string;
}

export async function putRecoveryKit(req: PutRecoveryKitRequest): Promise<void> {
    return api.put('/api/account/recoverykit', req);
}

// ── Account Unlock Sheet (password-reset plan §5.3.1; renamed and extended by
//    registration-restructure §12 — the sheet unlocks as well as resets) ──────

export interface ResetCardInfo {
    /** The epoch the newest sheet was sealed with. */
    epoch: number;
    /** The account's current reset_token_epoch — if it moved past `epoch`,
     *  the sheet on record is no longer valid. */
    currentEpoch: number;
    rskKeyId: number;
    tokenFingerprint: string;
    createdAt: string;
}

/** Returns null when the account has no Account Unlock Sheet on record. */
export async function getResetCard(): Promise<ResetCardInfo | null> {
    try {
        return await api.get('/api/account/reset-card');
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
    }
}

export interface IssueResetCardResult {
    /** The sealed CIDP envelope, base64 — render its raw bytes into the QR. */
    sealedToken: string;
    rskKeyId: number;
    epoch: number;
    createdAt: string;
}

/** Issue a new Account Unlock Sheet. Bump-on-issue: this permanently invalidates every
 *  previously issued sheet. The claim keys must be re-wrapped to the validator
 *  KEM key (same materials as claim validation). */
export async function issueResetCard(req: {
    emailClaimUuid: string;
    emailEncryptedKey: string;
    phoneClaimUuid: string;
    phoneEncryptedKey: string;
}): Promise<IssueResetCardResult> {
    return api.post('/api/account/reset-card/issue', req);
}

// ── Anonymous access-sheet flow (password-reset plan §5.3.2; §12) ────────────

export interface BeginResetResult {
    resetJobId: string;
    /** Capability held only by this (scanning) client — required for
     *  status/complete. The email landing device never sees it. */
    resetSecret: string;
    /** Phone: the handle to submit the SMS code against. Absent for email
     *  in production (the token rides only the emailed link). */
    verifyToken?: string | null;
}

/** Start a reset: typed CID + scanned sheet + chosen channel. A defective sheet
 *  or account yields an indistinguishable decoy — the flow simply never
 *  confirms. It runs at the account's origin, where the reset job lives: the
 *  job's status and completion go there too (pass the same `cid`). */
export async function beginReset(req: {
    cid: string;
    sealedToken: string; // base64 of the scanned envelope
    channel: 'email' | 'phone';
}): Promise<BeginResetResult> {
    return api.post('/api/account/reset/begin', req, { cid: req.cid, anonymous: true });
}

export type ResetJobStatus = 'pending' | 'code_sent' | 'confirmed' | 'consumed' | 'expired';

export async function resetStatus(
    req: { resetJobId: string; resetSecret: string }, cid: string,
): Promise<ResetJobStatus> {
    const resp = await api.post<{ status: ResetJobStatus }>('/api/account/reset/status', req, { cid, anonymous: true });
    return resp.status;
}

export interface CompleteResetRequest {
    resetJobId: string;
    resetSecret: string;
    /** Omit (or leave undefined) to keep the current password — §12 makes the
     *  password write optional so a "locked just in case" account can be
     *  unlocked without the owner having to learn a new password. */
    newAuthHash?: string;
    /** Mint a NEW Authenticator Key (registration-restructure §15.5). Default
     *  OFF — most unlocks are about a lock, not a lost phone. When true the
     *  response carries the new secret ONCE. */
    reissueTotp?: boolean;
}

export interface CompleteResetResult {
    /** The account was `locked` and is now `active`. */
    unlocked: boolean;
    /** A new password hash was written. */
    passwordChanged: boolean;
    /** A new Authenticator Key was minted; the login gate is OFF and the old
     *  sheet is void. */
    totpReissued?: boolean;
    /** Base32, 32 characters — returned ONCE, only with `totpReissued`. */
    totpSecret?: string;
    /** The `otpauth://` URI for the QR — returned ONCE, with the secret. */
    totpUri?: string;
}

/** The wire body for `complete`. Pure and exported for tests: the server tells
 *  "keep my password" from "set this password" by the PRESENCE of the field, so
 *  it must be absent — not null, not empty — when no password was chosen. */
export function buildCompleteResetBody(req: CompleteResetRequest): Record<string, string | boolean> {
    const body: Record<string, string | boolean> = {
        resetJobId: req.resetJobId,
        resetSecret: req.resetSecret,
    };
    if (req.newAuthHash) body.newAuthHash = req.newAuthHash;
    // Only when chosen: a completion that never mentions the Authenticator Key
    // must not read as one that declined a new one.
    if (req.reissueTotp) body.reissueTotp = true;
    return body;
}

/** Finish a confirmed ceremony: unlock the account, set a new password, reissue
 *  the Authenticator Key, or any combination. Never issues a session — the
 *  member logs in normally afterwards, and every completion revokes the
 *  account's existing sessions. */
export async function completeReset(req: CompleteResetRequest, cid: string): Promise<CompleteResetResult> {
    return api.post('/api/account/reset/complete', buildCompleteResetBody(req), { cid, anonymous: true });
}

/** Record a successful fire drill (§14). */
export async function markRecoveryKitTested(): Promise<void> {
    return api.post('/api/account/recoverykit/tested', {});
}

/** Point the kit record at a freshly exported bundle (§5.3). */
export async function updateRecoveryKitBundle(bundleFingerprint: string): Promise<void> {
    return api.post('/api/account/recoverykit/bundle', { bundleFingerprint });
}
